import type { CategoryCode } from '@patrianna/shared-patrianna-types/services/productCategories'
import type { ActiveRoom } from '@patrianna/shared-patrianna-types/store/BingoModule'
import type {
  GameProduct,
  GameProductErrorResponse,
  SwimlaneCategory,
} from '@patrianna/shared-patrianna-types/store/GameModule'
import type { Provider } from '@patrianna/shared-patrianna-types/store/ProviderModule'
import type { GetProductLayoutResponse } from '@patrianna/shared-patrianna-types/websocket/response'
import { getCookie } from '@patrianna/shared-utils/cookie'
import { isBrowser } from '@patrianna/shared-utils/helpers'
import * as Sentry from '@sentry/nextjs'
import type { GetServerSidePropsContext } from 'next'
import { cache } from 'react'

import { logErrorToSentry } from 'app/utils/logErrorToSentry'
import { getGameTypeData } from 'src/pageContainers/GameLandingPage/components/GameInfo/utils'
import { serviceDependencies } from 'src/services'
import { filterParlayMiniGames } from 'store/modules/games/utils'
import type { SupportedCurrencies } from 'types/currencies'

type FetchDataOptions = {
  silentStatuses?: number[]
}

async function fetchData(url: string, cookies?: string, headers?: Record<string, string>, options?: FetchDataOptions) {
  try {
    const response = await serviceDependencies.fetch(url, null, { method: 'get', cookies }, headers)

    const traceId = response.headers.get('x-trace-id')

    const contentType = response.headers.get('Content-Type')
    const isJson = contentType?.includes('application/json')

    if (response.ok && isJson) {
      return await response.json()
    }

    // log trace-id in Vercel logs in case of error
    if (traceId) {
      console.log(`trace-id: ${traceId}, url: ${url}`)
    }

    if (isJson) {
      const errorResponse = await response.json()
      const errorMessage = `Error response with body - ${JSON.stringify(errorResponse)}`
      const error = new Error(errorMessage)

      logErrorToSentry(error, 'http_500', {
        statusCode: response.status,
        pathname: url,
        platform: process.env.BRAND_NAME === 'pulsz' ? 'pulsz' : 'bingo',
      })

      return errorResponse
    } else {
      const statusCode = response.status
      const errorText = await response.text()
      const isSilentStatus = options?.silentStatuses?.includes(statusCode)

      if (isSilentStatus) {
        return {
          errorCode: statusCode,
          errorText: errorText || `Request failed with status ${statusCode}`,
          statusCode,
        }
      }

      throw new Error(`Error response without body - ${errorText || 'empty'}`)
    }
  } catch (err) {
    logErrorToSentry(err, 'http_500', {
      pathname: url,
      platform: process.env.BRAND_NAME === 'pulsz' ? 'pulsz' : 'bingo',
    })

    throw err
  }
}

export async function getAllGames(cookies?: string, headers?: Record<string, string>): Promise<GameProduct[]> {
  const path = typeof window === 'undefined' ? process.env.SERVER_FETCH_URL : process.env.GATEWAY
  const url = `${path}/v1/get-all-products?brandName=${process.env.BRAND_NAME}`
  const result = await fetchData(url, cookies, headers)

  return filterParlayMiniGames(result.products)
}

export async function getProviderURL({ apiUrl, body }: any) {
  const fetchPragmaticData = async () => {
    return await serviceDependencies.fetch(apiUrl as string, body, { method: 'POST' })
  }

  try {
    const response = await fetchPragmaticData()
    const { gameUrl } = await response.json()

    if (gameUrl) {
      return gameUrl
    }
  } catch (error) {
    console.log({ error })
  }

  return ''
}

export async function getMiniGames(cookies?: string, headers?: Record<string, string>): Promise<GameProduct[]> {
  try {
    const allGames = await getAllGames(cookies, headers)
    const miniGames = allGames.filter((game: GameProduct) => game.miniMode)

    return miniGames
  } catch (error) {
    console.log('getMiniGames error', { error })

    return []
  }
}

export async function getAllCategories(
  cookies?: string,
  headers?: Record<string, string> | string,
): Promise<{ categories: SwimlaneCategory[]; layout: string }> {
  const path = typeof window === 'undefined' ? process.env.SERVER_FETCH_URL : process.env.GATEWAY
  const url = `${path}/v1/product-layout?brandName=${process.env.BRAND_NAME}`
  const pulszaidId = getCookie('PULSZAID', cookies)

  const headersParsed = typeof headers === 'string' ? JSON.parse(headers) : headers

  const result = await fetchData(url, cookies, headersParsed)
  if (result) {
    // console.log('/product-layout response: ', {
    //   ...result,
    //   categories: result?.categories?.map((i: SwimlaneCategory) => ({ ...i, products: i.products.length })),
    // })
  }

  // show response details in Vercel logs
  try {
    if (result) {
      // console.log('/product-layout response: ', {
      //   ...result,
      //   categories: result?.categories?.map((i: SwimlaneCategory) => ({ ...i, products: i.products.length })),
      // })
    }

    if (!result?.categories?.length) {
      Sentry.setUser({
        id: pulszaidId,
      })
      Sentry.captureMessage(`/product-layout empty response - ${JSON.stringify(result)}`)
    }

    if (!result?.categories?.length) {
      Sentry.setUser({
        id: pulszaidId,
      })
      Sentry.captureMessage(`/product-layout empty response - ${JSON.stringify(result)}`)
    }
  } catch (error) {
    console.error('Error while logging response details: ', error)
  }

  const filteredCategories = (result?.categories || []).map((category: SwimlaneCategory) => ({
    ...category,
    products: filterParlayMiniGames(category.products as GameProduct[]),
  }))
  const layout = result?.layout

  return { categories: filteredCategories, layout }
}

export async function getCategoryByRoute(
  categoryRoute: string,
  cookies?: string,
  headers?: Record<string, string>,
): Promise<{ category: SwimlaneCategory; layout: string }> {
  const { categories, layout } = await getAllCategories(cookies, headers)

  const category = categories.find(
    (i: SwimlaneCategory) =>
      i.route === `/slots/${categoryRoute ? `${categoryRoute}/` : ''}` ||
      i.route === `/categories/${categoryRoute ? `${categoryRoute}/` : ''}`,
  )

  return { category, layout }
}

export async function getTableGamesCategory(cookie?: string, headers?: Record<string, string>) {
  const { categories } = await getAllCategories(cookie, headers)
  const category = categories?.find((i) => i.route === '/table-games/')

  return category
}

export async function getBingoCategory(cookies?: string, headers?: Record<string, string>): Promise<SwimlaneCategory> {
  const { categories } = await getAllCategories(cookies, headers)

  const bingoCategory = categories?.find((el: SwimlaneCategory) => el.type === 'bingo')

  return bingoCategory
}

export async function getPreviouslyPlayedCategory(
  cookies?: string,
  headers?: Record<string, string>,
): Promise<{ category: SwimlaneCategory; layout: string }> {
  const { categories, layout } = await getAllCategories(cookies, headers)

  const previouslyPlayedCategory = categories.find(
    (i: SwimlaneCategory) => i.route.indexOf('/previously-played') !== -1,
  )

  return { category: previouslyPlayedCategory, layout }
}

export const CACHE_GAME_BY_CODE: Record<string, GameProduct> = {}

export async function getGameByCode(code: string): Promise<GameProduct | GameProductErrorResponse> {
  if (CACHE_GAME_BY_CODE[code]) {
    return CACHE_GAME_BY_CODE[code]
  }

  const path = typeof window === 'undefined' ? process.env.SERVER_FETCH_URL : process.env.GATEWAY
  const url = `${path}/v1/get-product?brandName=${process.env.BRAND_NAME}&code=${code}`
  const game = await fetchData(url, undefined, undefined, {
    silentStatuses: [404, 403],
  })

  if (!(game as GameProduct & GameProductErrorResponse).errorCode) {
    CACHE_GAME_BY_CODE[code] = game as GameProduct
  }

  if (isGameProductErrorResponse(game) && Number(game.errorCode) === 404) {
    return {
      ...game,
      errorText: 'Game not found',
    }
  }

  if (isGameProductErrorResponse(game) && Number(game.errorCode) === 403) {
    return {
      ...game,
      errorText: 'Game not available',
    }
  }

  return game
}

export function isGameProductErrorResponse(
  game: GameProduct | GameProductErrorResponse,
): game is GameProductErrorResponse {
  return Boolean((game as GameProductErrorResponse).errorCode)
}

type GetGameByRouteArgs = {
  route: string
  cookies?: string
  showAll?: boolean
}

const fetchGameByRoute = async ({
  route,
  cookies,
  showAll,
}: GetGameByRouteArgs): Promise<GameProduct | GameProductErrorResponse> => {
  const base = typeof window === 'undefined' ? process.env.SERVER_FETCH_URL : process.env.GATEWAY

  const params = new URLSearchParams({
    brandName: String(process.env.BRAND_NAME ?? ''),
    route,
    ...(showAll ? { showAll: 'true' } : {}),
  })

  const url = `${base}/v1/get-product?${params.toString()}`

  const game = await fetchData(url, cookies, undefined, {
    silentStatuses: [404, 403],
  })

  if (isGameProductErrorResponse(game) && Number(game.errorCode) === 404) {
    return {
      ...game,
      errorText: `Game ${route} not found`,
    }
  }

  if (isGameProductErrorResponse(game) && Number(game.errorCode) === 403) {
    return {
      ...game,
      errorText: `Game ${route} not available`,
    }
  }

  return game
}

const getGameByRouteCached = cache(({ route, showAll }: Omit<GetGameByRouteArgs, 'cookies'>) =>
  fetchGameByRoute({ route, showAll }),
)

export async function getGameByRoute(
  route?: string,
  cookies?: string,
  showAll?: boolean,
): Promise<GameProduct | GameProductErrorResponse> {
  const normalizedRoute = route?.trim()

  if (!normalizedRoute || normalizedRoute === 'undefined') {
    return {
      errorCode: 'invalid_route',
      errorText: 'Game route is missing',
    } as GameProductErrorResponse
  }

  if (typeof window === 'undefined') {
    if (cookies) {
      // Server-side, user-specific cookies: bypass cache to avoid leaking responses across users
      return fetchGameByRoute({ route: normalizedRoute, cookies, showAll })
    }

    // Server-side, no cookies: safe to use cache keyed by route/showAll
    return getGameByRouteCached({ route: normalizedRoute, showAll })
  }

  // Client-side: always fetch fresh; React cache is server-only
  return fetchGameByRoute({ route: normalizedRoute, cookies, showAll })
}

const dict = {
  pulsz: 'Pulsz',
  pulszbingo: 'Pulszbingo',
}

// TODO: ['casino', 'casino_games'] for backward compatibility
const contenDescriptionDict = {
  slots: 'online slot',
  video_slots: 'online slot',
  classic_slots: 'online slot',
  scratch_card: 'online slot',
  table: 'table game',
  table_games: 'table game',
  casino: 'table game',
  casino_games: 'table game',
  blackjack: 'table game',
  video_poker: 'poker',
  roulette: 'roulette',
  baccarat: 'baccarat',
  baccarat_new: 'baccarat',
  bingo: 'bingo',
  in_house: 'casual game',
}

const volatilityDict = {
  1: 'low',
  2: 'medium',
  3: 'high',
}

const getCategoryLink = (gameProduct: GameProduct) => {
  const { categoryLink } = getGameTypeData(gameProduct)

  return categoryLink
}

export const generateDefaultSlingoContent = (gameProduct: any) => {
  const { title, sweepstakeMaxWinMultiplier } = gameProduct
  const maxWin =
    sweepstakeMaxWinMultiplier !== undefined ? `x${sweepstakeMaxWinMultiplier}` : sweepstakeMaxWinMultiplier

  let slingoDescriptionTemplate = `<p>Play ${title} and relive the nostalgia of classic casual casino games, now with thrilling multipliers and big win potential! Every round delivers fast-paced action, giving you the chance to boost your winnings and see rewards soar—all the way up to ${maxWin}. Ready for the challenge? Just hit the 'Play Now' button and see how high you can score and win huge prizes!</p>`

  if (!maxWin) {
    slingoDescriptionTemplate = `<p>Play ${title} and relive the nostalgia of classic casual casino games, now with thrilling multipliers and big win potential! Every round delivers fast-paced action, giving you the chance to boost your winnings and see rewards soar. Ready for the challenge? Just hit the 'Play Now' button and see how high you can score and win huge prizes!</p>`
  }

  return `
    <h1>${title}</h1>
    ${slingoDescriptionTemplate}
`
}

export const generateDefaultGameContent = (gameProduct: any) => {
  const { title, type, volatility, sweepstakeMaxWinMultiplier } = gameProduct

  const categoryLink = getCategoryLink(gameProduct)
  const description = contenDescriptionDict[type as keyof typeof contenDescriptionDict]
  const volatilityDescription = volatilityDict[volatility as keyof typeof volatilityDict]
  const maxWin =
    sweepstakeMaxWinMultiplier !== undefined ? `x${sweepstakeMaxWinMultiplier}` : sweepstakeMaxWinMultiplier

  let gameDescriptionTemplate = `<p>
      Jump into this thrilling <a href="${categoryLink}">${description}</a>, featuring exciting gameplay with
      ${volatilityDescription} volatility and cutting-edge features.
      With a max win of up to ${maxWin}, the thrill of big rewards awaits at every turn!
    </p>`

  if (!maxWin && !volatilityDescription) {
    gameDescriptionTemplate = `<p>
      Jump into this thrilling <a href="${categoryLink}">${description}</a>, featuring exciting gameplay with cutting-edge features.
      The thrill of big rewards awaits at every turn!
    </p>`
  }

  if (maxWin && !volatilityDescription) {
    gameDescriptionTemplate = `<p>
      Jump into this thrilling <a href="${categoryLink}">${description}</a>, featuring exciting gameplay with cutting-edge features.
      With a max win of up to ${maxWin}, the thrill of big rewards awaits at every turn!
    </p>`
  }

  if (!maxWin && volatilityDescription) {
    gameDescriptionTemplate = `<p>
      Jump into this thrilling <a href="${categoryLink}">${description}</a>, featuring exciting gameplay with
      ${volatilityDescription} volatility and cutting-edge features.
      The thrill of big rewards awaits at every turn!
    </p>`
  }

  return `
    <h1>${title}</h1>
    ${gameDescriptionTemplate}
  `
}

export async function generateMetaData(gameProduct: any) {
  const brandName = dict[process.env.BRAND_NAME as keyof typeof dict]

  const getGameType = () => {
    switch (true) {
      case (gameProduct.tags ?? []).includes('table'):
        return 'Table'
      case (gameProduct.tags ?? []).includes('bingo'):
        return 'Bingo'
      default:
        return 'Slot'
    }
  }

  const generateTitle = (gameType: string, gameTitle: string) => {
    switch (gameType) {
      case 'scratch_card':
        return `Play ${gameTitle} Scratchcard Online | ${brandName} USA`
      case 'in_house':
        return `Play ${gameTitle} Casual Game Online | ${brandName} USA`
      case 'blackjack':
      case 'poker':
        return `Play ${gameTitle} Online | ${brandName} USA`
      case 'bingo':
        return `${gameTitle} | Play Free Online Bingo & Social Casino Games | PulszBingo USA`
      default:
        return `${gameProduct.title} ${getGameType()}: Play for Fun | ${brandName} USA`
    }
  }

  const generateDesc = (gameType: string, gameTitle: string, maxWin: number) => {
    const isMaxWin = maxWin ? ` up to ${maxWin}x` : ''
    switch (gameType) {
      case 'scratch_card':
        return `Play ${gameTitle} scratch-off game on ${brandName} and enjoy instant wins! Uncover hidden prizes up to ${maxWin}x with every scratch!`
      case 'in_house':
        return `Play ${gameTitle} casual game on ${brandName}! Enjoy fast action and win up to ${maxWin}x. Aim for high scores and big prizes!`
      case 'blackjack':
        return `Play ${gameTitle} on ${brandName}! Enjoy this classic table game, hit 21, and win big. Experience exciting gameplay and top action!`
      case 'poker':
        return `Join our ${gameTitle} table on ${brandName}! Test your skills and win big. Enjoy thrilling gameplay and various online poker variants!`
      case 'bingo':
        return 'Explore top bingo games such as Club 75, Feature 90 and Freeplay Heaven. Join PulszBingo now for daily rewards and more social casino games.'
      default:
        return `Play ${gameProduct.title} online ${getGameType().toLowerCase()} at ${brandName}!
          Spin the reels and experience exciting features and big wins${isMaxWin}.
          Play now for fun!`
    }
  }

  return {
    title: generateTitle(gameProduct.type, gameProduct.title),
    ogTitle: generateTitle(gameProduct.type, gameProduct.title),
    meta: generateDesc(gameProduct.type, gameProduct.title, gameProduct.sweepstakeMaxWinMultiplier),
    keyword: '',
  }
}

export async function loadGameInfoPageContent(
  ctx: GetServerSidePropsContext,
  currency: SupportedCurrencies,
  isContentDisabled?: boolean,
) {
  const gameProduct = await getGameByRoute(ctx.params.gameId as string, ctx.req.headers.cookie)

  if (isGameProductErrorResponse(gameProduct)) {
    return {
      props: {
        currency,
      },
      notFound: true,
    }
  }

  const pageContent = isContentDisabled ? null : generateDefaultGameContent(gameProduct)
  const meta = await generateMetaData(gameProduct)

  return {
    props: {
      gameProduct,
      currency,
      meta: { ...meta },
      pageContent,
    },
    notFound: !gameProduct.code,
  }
}

export async function loadGameStaticProps(gameId: string, currency: SupportedCurrencies) {
  const REVALIDATE_TIME = 180
  try {
    const gameProduct = await getGameByRoute(gameId, undefined, true)

    if (isGameProductErrorResponse(gameProduct)) {
      throw gameProduct
    }

    const meta = await generateMetaData(gameProduct)

    return {
      props: {
        gameProduct,
        meta,
        currency,
      },
      notFound: !gameProduct?.route,
      ...(!gameProduct?.route && {
        revalidate: REVALIDATE_TIME,
      }),
    }
  } catch (error) {
    if (isGameProductErrorResponse(error)) {
      return {
        props: {
          error: error.errorText,
        },
        notFound: true,
        revalidate: REVALIDATE_TIME,
      }
    }

    return {
      props: {
        error: 'Page Not Found 404',
      },
    }
  }
}

export function limitSwimlaneGames(categories: SwimlaneCategory[]) {
  const GAMES_PER_LINE = 6

  return categories.map((i) => {
    const maxGames = Math.round(GAMES_PER_LINE * i.lines + GAMES_PER_LINE)

    return {
      ...i,
      products: i.products.slice(0, maxGames),
    }
  })
}

export function getBingoRooms(brand: string, rooms: [void | ActiveRoom[], void | ActiveRoom[]]) {
  if (brand !== 'pulszbingo') {
    return {}
  }

  return rooms?.flat() || {}
}

export function setTopGames(
  count: number,
  setIsLoading: (isLoading: boolean) => void,
  setGames: (category: any) => void,
  setLayout: (layout: string) => void,
) {
  getCategoryByRoute('top')
    .then(({ category: topCategory, layout }) => {
      if (topCategory?.products.length) {
        setGames(topCategory.products.slice(0, count) as GameProduct[])
      }
      if (layout) {
        setLayout(layout)
      }
    })
    .finally(() => {
      setIsLoading(false)
    })
}

// for categories pages
export async function getGamesByCategoryCode(
  placements: {
    productCategoryCode: CategoryCode | string
    gameId?: string
  }[],
  cookies?: string,
  headers?: Record<string, string>,
): Promise<GetProductLayoutResponse & { layout: string }> {
  const path = !isBrowser() ? process.env.SERVER_FETCH_URL : process.env.GATEWAY
  const url = `${path}/v1/product-category?brandName=${process.env.BRAND_NAME}`
  const body = { placements }

  try {
    const data = await serviceDependencies.fetch(url, body, { cookies }, headers)
    console.log(`trace-id: ${data.headers.get('x-trace-id')}, url: ${url}`)

    if (!data.ok) {
      return null
    }

    const json = await data.json()
    const filteredCategories = (json?.categories || []).map((category: SwimlaneCategory) => ({
      ...category,
      products: filterParlayMiniGames(category.products as GameProduct[]),
    }))

    return { ...json, categories: filteredCategories }
  } catch (error) {
    console.error(`Fetch ${getGamesByCategoryCode.name} function error: `, 'URL', url, 'Error', error?.message)

    return null
  }
}

export async function getAllProviders(
  cookies?: string,
  headers?: Record<string, string>,
): Promise<{ providers: Provider[]; nextModeDateTime?: GameProduct['nextModeDateTime'] }> {
  const path = typeof window === 'undefined' ? process.env.SERVER_FETCH_URL : process.env.GATEWAY
  const url = `${path}/v1/brands/${process.env.BRAND_NAME}/suppliers`
  try {
    const response = await serviceDependencies.fetch(url, null, { method: 'get', cookies }, headers)
    console.log(`trace-id: ${response?.headers?.get('x-trace-id')}, url: ${url}`)
    const result = await response.json()
    const providers = (result?.suppliers || []).map((supplier: Provider) => ({
      ...supplier,
      products: filterParlayMiniGames(supplier.products as any),
    }))

    // show response details in Vercel logs
    try {
      if (result) {
        // console.log('/suppliers response: ', {
        //   ...result,
        //   suppliers: result?.suppliers?.map((i: SwimlaneCategory) => ({ ...i, products: i.products.length })),
        // })
      }
    } catch (error) {
      console.error('Error while logging response details: ', error)
    }

    return { providers, nextModeDateTime: result.nextModeDateTime }
  } catch (error) {
    console.error(`Fetch ${getAllProviders.name} function error: `, 'URL', url, 'Error', error?.message)

    return { providers: [], nextModeDateTime: '' }
  }
}

export async function getProviderByCode(
  code: string,
  cookies?: string,
  headers?: Record<string, string>,
): Promise<{ provider: Provider; nextModeDateTime?: GameProduct['nextModeDateTime'] }> {
  const path = typeof window === 'undefined' ? process.env.SERVER_FETCH_URL : process.env.GATEWAY
  const url = `${path}/v1/brands/${process.env.BRAND_NAME}/suppliers/${code}`
  try {
    const response = await serviceDependencies.fetch(url, null, { method: 'get', cookies }, headers)
    console.log(`trace-id: ${response?.headers?.get('x-trace-id')}, url: ${url}`)

    if (!response.ok) {
      return null
    }

    const result = await response.json()
    const provider = result?.supplier
      ? {
          ...result.supplier,
          products: filterParlayMiniGames(result.supplier.products as any),
        }
      : undefined

    // show response details in Vercel logs
    try {
      if (result) {
        // console.log(`/suppliers/${code} response: `, JSON.stringify(result))
      }
    } catch (error) {
      console.error('Error while logging response details: ', error)
    }

    return { provider, nextModeDateTime: result.nextModeDateTime }
  } catch (error) {
    console.error(`Fetch ${getProviderByCode.name} function error: `, 'URL', url, 'Error', error?.message)

    return null
  }
}
